<template>
  <div
    class="h-96 border border-gray-200 rounded-lg cursor-pointer shadow-md bg-white transition-all duration-200 hover:border-primary-community hover:shadow-lg"
    :class="{'!cursor-default': disabled }"
    @click="visitPost"
  >
    <!-- Post Thumbnail -->
    <div class="h-44 w-full rounded-t-lg relative overflow-hidden bg-gray-200">
      <!-- Loading placeholder -->
      <div
        v-if="!imageLoaded && !imageError"
        class="absolute inset-0 bg-gray-200 animate-pulse tw-flex items-center justify-center"
      >
        <div class="text-gray-400">
          <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"/>
          </svg>
        </div>
      </div>

      <!-- Actual image -->
      <img
        :src="postThumbnailUrl"
        :alt="post.title || 'Post thumbnail'"
        class="w-full h-full object-cover transition-opacity duration-300"
        :class="{ 'opacity-0': !imageLoaded, 'opacity-100': imageLoaded }"
        loading="lazy"
        @load="onImageLoad"
        @error="onImageError"
      />
      <!-- Bookmark/Action Button -->
      <div class="absolute top-3 right-3 z-1" @click.stop>
        <BookmarkButton
          v-if="isLoggedIn && !disabled"
          :post-id="post.id"
          :is-bookmarked="isBookmarked"
          :bookmarks-count="bookmarksCount"
          :community-id="communityId"
          :show-label="false"
          :show-count="false"
          variant="rounded-secondary"
          @bookmarkToggled="handleBookmarkToggled"
        />
      </div>

      <!-- Creator Avatar -->
      <div
        v-if="post.creator && post.creator.avatar_url"
        class="absolute right-4 -bottom-6 w-12 h-12"
      >
        <ActorAvatar :actor="post.creator" :actor-logo-url="post.creator.avatar_url" :width="48"/>
      </div>

      <!-- File count indicator -->
      <div
        v-if="post.uploaded_content && post.uploaded_content.length > 0"
        class="absolute bottom-3 left-3 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded-full"
      >
        {{ post.uploaded_content.length }} {{ post.uploaded_content.length === 1 ? 'file' : 'files' }}
      </div>
    </div>

    <!-- Post Content -->
    <div class="p-4 h-52 flex flex-col">
      <!-- Title -->
      <h3 class="text-lg font-semibold text-gray-900 mb-3 line-clamp-2 leading-tight">
        {{ post.title }}
      </h3>

      <!-- Excerpt -->
      <p class="text-sm text-gray-600 mb-4 line-clamp-3 flex-1">
        {{ postExcerpt }}
      </p>

      <!-- Footer -->
      <div class="tw-flex items-center justify-between text-xs text-gray-500 mt-auto">
        <div class="tw-flex items-center space-x-2">
          <AvatarNew :user="post.creator" show-details size="small"/>
          <span>•</span>
          <span>{{ formatDate(post.createdAt) }}</span>
        </div>
        <div class="tw-flex items-center space-x-2">
          <!-- Category badge -->
          <!--          <span
                      v-if="post.category"
                      class="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs"
                    >
                      {{ post.category.name }}
                    </span>-->
          <!-- Public/Private indicator -->
          <!--          <Icon
                      :name="post.is_public ? 'globe' : 'lock'"
                      :class="post.is_public ? 'text-green-500' : 'text-gray-400'"
                      class="w-4 h-4"
                    />-->
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import Icon from '@/components/Icon/Icon.vue'
  import ActorAvatar from '@/Domain/Actor/Components/ActorAvatar.vue'
  import BookmarkButton from './BookmarkButton.vue'
  import AvatarNew from '@/components/Avatar/AvatarNew.vue'
  import { getDefaultFallbackImageUrl } from '@/util/helpers'
  import { trackMatomoEvent } from '@/util/analytics'
  import { MATOMO_EVENT_ACTIONS, MATOMO_EVENT_CATEGORIES } from '@/constants/analytics-constants'
  import { logUserActivity } from '@/util/user-analytics'
  import UserMixin from '@/util/UserMixin'

  export default defineComponent({
    name: 'KnowledgeBasePostCard',
    components: {
      AvatarNew,
      Icon,
      ActorAvatar,
      BookmarkButton
    },
    props: {
      post: {
        type: Object,
        required: true
      },
      showActions: {
        type: Boolean,
        default: false
      },
      currentUserId: {
        type: Number,
        default: null
      },
      communityId: {
        type: [String, Number],
        default: null
      },
      spottingAreaId: {
        type: [String, Number],
        default: null
      },
      disabled: {
        type: Boolean,
        default: false,
      }
    },
    emits: ['edit-post', 'delete-post', 'bookmark-toggled'],
    mixins: [UserMixin],
    data() {
      return {
        imageLoaded: false,
        imageError: false
      }
    },
    computed: {
      postThumbnailUrl() {
        if (this.post.thumbnail) {
          return this.post.thumbnail
        }

        // Try to get first image from uploaded content
        if (this.post.uploaded_content) {
          const firstImage = this.post.uploaded_content.find(file =>
            file.type && file.type.startsWith('image/')
          )
          if (firstImage && firstImage.url) {
            return firstImage.url
          }
        }

        // Default placeholder
        return getDefaultFallbackImageUrl()
      },

      postExcerpt() {
        if (this.post.excerpt) {
          return this.post.excerpt
        }

        // Generate excerpt from body
        const plainText = this.post.body.replace(/<[^>]*>/g, '')
        return plainText.length > 150
          ? plainText.substring(0, 150) + '...'
          : plainText
      },

      canUserEdit() {
        return this.currentUserId && this.post.creator_id === this.currentUserId
      },

      isBookmarked() {
        return this.post.isBookmarked
      },

      bookmarksCount() {
        return this.$store.getters.getPostBookmarksCount(this.post.id) || this.post.bookmarks_count || 0
      }
    },
    methods: {
      formatDate(dateString: string) {
        const date = new Date(dateString)
        const now = new Date()
        const diffInMs = now.getTime() - date.getTime()
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

        if (diffInDays === 0) {
          return this.$t('knowledge_base_today')
        } else if (diffInDays === 1) {
          return this.$t('knowledge_base_1_day_ago')
        } else if (diffInDays < 30) {
          return `${diffInDays} ${this.$t('knowledge_base_days_ago')}`
        } else if (diffInDays < 365) {
          const months = Math.floor(diffInDays / 30)
          return months === 1 ? this.$t('knowledge_base_1_month_ago') : `${months} ${this.$t('knowledge_base_months_ago')}`
        } else {
          return date.toLocaleDateString()
        }
      },

      visitPost() {
        if (this.disabled) {
          return
        }

        if (this.communityId) {

          try {
            trackMatomoEvent(MATOMO_EVENT_CATEGORIES.KNOWLEDGE_BASE, MATOMO_EVENT_ACTIONS.VISIT_RESOURCE, this.post.id)
            logUserActivity(`/communities/${this.communityId}/knowledge-base/posts/${this.post.id}`, 'knowledge-base-detail', this.post.id)
          } catch (error) {
            console.log(error)
          }

          this.$router.push(`/communities/${this.communityId}/knowledge-base/posts/${this.post.id}`)

          return
        }

        if (this.$route.params.spottingAreaId) {

          try {
            trackMatomoEvent(MATOMO_EVENT_CATEGORIES.KNOWLEDGE_BASE, MATOMO_EVENT_ACTIONS.VISIT_RESOURCE, this.post.id)
            logUserActivity(`/spotting-areas/${this.$route.params.spottingAreaId}/knowledge-base/posts/${this.post.id}`, 'knowledge-base-detail', this.post.id)
          } catch (error) {
            console.log(error)
          }

          this.$router.push(`/spotting-areas/${this.$route.params.spottingAreaId}/knowledge-base/posts/${this.post.id}`)

          return
        }

        try {
          trackMatomoEvent(MATOMO_EVENT_CATEGORIES.KNOWLEDGE_BASE, MATOMO_EVENT_ACTIONS.VISIT_RESOURCE, this.post.id)
          logUserActivity(`/knowledge-base/posts/${this.post.id}`, 'knowledge-base-detail', this.post.id)
        } catch (error) {
          console.log(error)
        }

        this.$router.push(`/knowledge-base/posts/${this.post.id}`)
      },

      handleBookmarkToggled(data) {
        // Track bookmark action
        const action = data.isBookmarked ? MATOMO_EVENT_ACTIONS.BOOKMARK_POST : MATOMO_EVENT_ACTIONS.UNBOOKMARK_POST
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.KNOWLEDGE_BASE, action, this.post.title)

        // Update the store state
        this.$store.dispatch('updatePostBookmarkStatus', data)
        // Also emit for parent components that might need to know
        this.$emit('bookmark-toggled', {
          ...this.post,
          is_bookmarked: data.isBookmarked,
          bookmarks_count: data.bookmarksCount
        })
      },

      editPost() {
        this.$emit('edit-post', this.post)
      },

      deletePost() {
        this.$emit('delete-post', this.post)
      },

      onImageLoad() {
        this.imageLoaded = true
        this.imageError = false
      },

      onImageError() {
        this.imageError = true
        this.imageLoaded = false
      }
    }
  })
</script>

<style scoped>
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
</style>
